import { useEffect, useRef, useState } from 'react'

import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { Text } from '@mxenabled/mxui'

import { Dialog } from '../../common'

const CHARACTER_LIMIT = 250

export type FeedbackDialogCloseReason = 'closeButton' | 'backdropClick' | 'escapeKeyDown'

export interface FeedbackDialogProps {
  title: string
  options: string[]
  characterLimit?: number
  characterLimitLabel?: string
  isOpen: boolean
  onSubmit: (option: string, detail?: string) => void
  /** Called on every close; `reason` is set for user-initiated closes (X/backdrop/escape), undefined on submit/back. */
  onClose: (reason?: FeedbackDialogCloseReason) => void
  onBackClick?: () => void
  onTextInputFocus?: () => void
  secondaryText: string
  primaryText: string
  closeAriaLabel: string
}

/** Generic feedback survey dialog. Last option shows detail text field. */
const FeedbackDialog = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  options,
  characterLimit = CHARACTER_LIMIT,
  characterLimitLabel,
  secondaryText,
  primaryText,
  closeAriaLabel,
  onBackClick,
  onTextInputFocus,
}: FeedbackDialogProps) => {
  const [selected, setSelected] = useState('')
  const [detail, setDetail] = useState('')
  const hasTrackedFocus = useRef(false)
  const skipCloseAnalytics = useRef(false)

  useEffect(() => {
    if (isOpen) {
      hasTrackedFocus.current = false
      skipCloseAnalytics.current = false
    }
  }, [isOpen])

  if (options.length === 0) return null

  const isOther = selected === options[options.length - 1]
  const isSubmitDisabled = !selected || (isOther && !detail.trim())

  const reset = () => {
    setSelected('')
    setDetail('')
  }

  const resolveCloseReason = (reason?: string): FeedbackDialogCloseReason | undefined => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return reason
    if (!skipCloseAnalytics.current) return 'closeButton'
    return undefined
  }

  const handleClose = (_event?: unknown, reason?: string) => {
    const closeReason = resolveCloseReason(reason)
    reset()
    onClose(closeReason)
  }

  const handleBack = () => {
    skipCloseAnalytics.current = true
    onBackClick?.()
  }

  const handleTextFocus = () => {
    if (!hasTrackedFocus.current) {
      hasTrackedFocus.current = true
      onTextInputFocus?.()
    }
  }

  const handleSubmit = () => {
    skipCloseAnalytics.current = true
    onSubmit(selected, isOther ? detail.trim() : undefined)
    reset()
  }

  return (
    <Dialog
      copy={{ title, close_aria: closeAriaLabel }}
      disablePrimaryButton={isSubmitDisabled}
      isOpen={isOpen}
      onClose={handleClose}
      onPrimaryAction={handleSubmit}
      onSecondaryAction={handleBack}
      primaryText={primaryText}
      secondaryText={secondaryText}
      sx={{
        '& .MuiDialogContent-root': { py: 0 },
        '& .MuiDialog-paperScrollPaper': { bgcolor: 'background.paper' },
        '& .MuiDialog-paper': {
          outline: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
          },
        },
        '& .MuiButton-outlined': {
          border: 'none',
          '&:hover': { bgcolor: 'transparent', border: 'none' },
        },
      }}
    >
      <FormControl>
        <RadioGroup
          aria-label={title}
          name="feedback-radio-group"
          onChange={(e) => setSelected(e.target.value)}
          value={selected}
        >
          {options.map((option) => (
            <FormControlLabel
              control={
                <Radio
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 22,
                      color: 'action.active',
                    },
                  }}
                />
              }
              key={option}
              label={option}
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: 'text.primary',
                  fontSize: 15,
                },
              }}
              value={option}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {isOther && (
        <Stack sx={{ gap: 3, mt: 4 }}>
          <TextField
            autoFocus
            maxRows={4}
            minRows={4}
            multiline={true}
            onChange={(e) => setDetail(e.target.value)}
            onFocus={handleTextFocus}
            slotProps={{ htmlInput: { maxLength: characterLimit } }}
            value={detail}
          />
          {characterLimitLabel && (
            <Text sx={{ color: 'text.secondary', fontWeight: 400 }} variant="subtitle2">
              {characterLimitLabel}
            </Text>
          )}
        </Stack>
      )}
    </Dialog>
  )
}

export default FeedbackDialog
